import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { z } from 'zod';

const messageSchema = z.object({
  subject: z.string().trim().min(1, "Le sujet est requis").max(200, "Le sujet ne peut pas dépasser 200 caractères"),
  message: z.string().trim().min(1, "Le message est requis").max(2000, "Le message ne peut pas dépasser 2000 caractères")
});

interface ContactRelayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  relayId: string;
  relayName: string;
}

export const ContactRelayDialog = ({ open, onOpenChange, relayId, relayName }: ContactRelayDialogProps) => {
  const { user, profile } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [canContact, setCanContact] = useState<boolean | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(true);

  // Check if user can contact this relay
  const checkContactPermission = useCallback(async () => {
    if (!user?.id || !relayId) {
      setCanContact(false);
      setCheckingPermission(false);
      return;
    }

    try {
      // Check if user is admin or super_admin
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('system_role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'super_admin']);

      if (userRoles && userRoles.length > 0) {
        setCanContact(true);
        setCheckingPermission(false);
        return;
      }

      // Get relay manager_id
      const { data: relay } = await supabase
        .from('relays')
        .select('manager_id')
        .eq('id', relayId)
        .single();

      if (!relay?.manager_id) {
        setCanContact(false);
        setCheckingPermission(false);
        return;
      }

      // Check if user has orders at this relay
      const { data: orders, error } = await supabase
        .from('orders')
        .select('id')
        .eq('client_id', user.id)
        .eq('relay_id', relayId)
        .limit(1);

      if (error) throw error;

      setCanContact(orders && orders.length > 0);
    } catch (error) {
      console.error('Error checking contact permission:', error);
      setCanContact(false);
    } finally {
      setCheckingPermission(false);
    }
  }, [user?.id, relayId]);

  // Check permission when dialog opens
  useEffect(() => {
    if (open) {
      setCheckingPermission(true);
      checkContactPermission();
    }
  }, [open, checkContactPermission]);

  const handleSend = async () => {
    if (!user?.id) {
      toast.error('Vous devez être connecté pour envoyer un message');
      return;
    }

    if (!canContact) {
      toast.error('Vous ne pouvez contacter ce point relais que si vous avez une commande avec eux');
      return;
    }

    // Validate inputs with zod
    const validation = messageSchema.safeParse({ subject, message });
    if (!validation.success) {
      const errors = validation.error.issues.map(e => e.message).join(', ');
      toast.error(errors);
      return;
    }

    setLoading(true);
    try {
      // Récupérer le manager_id du point relais
      const { data: relay, error: relayError } = await supabase
        .from('relays')
        .select('manager_id')
        .eq('id', relayId)
        .single();

      if (relayError) throw relayError;

      if (!relay?.manager_id) {
        toast.error('Ce point relais n\'a pas de responsable assigné');
        setLoading(false);
        return;
      }

      // Envoyer le message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: relay.manager_id,
          subject: `[Point Relais: ${relayName}] ${subject}`,
          content: message,
        });

      if (messageError) throw messageError;

      toast.success('Message envoyé avec succès');
      setSubject('');
      setMessage('');
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      // Check if it's an RLS policy violation
      if (error.message?.includes('row-level security') || error.code === '42501') {
        toast.error('Vous ne pouvez contacter ce point relais que si vous avez une commande avec eux');
      } else {
        toast.error('Erreur lors de l\'envoi du message');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Contacter le point relais</DialogTitle>
          <DialogDescription>
            Envoyez un message au responsable de {relayName}
          </DialogDescription>
        </DialogHeader>

        {checkingPermission ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : !canContact ? (
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Vous ne pouvez contacter ce point relais que si vous avez une commande en cours ou passée avec eux.
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                Passez d'abord une commande avec livraison à ce point relais pour pouvoir les contacter.
              </p>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Sujet (max 200 caractères)</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ex: Question sur les horaires"
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground">{subject.length}/200</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message (max 2000 caractères)</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Votre message..."
                  rows={6}
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground">{message.length}/2000</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button onClick={handleSend} disabled={loading}>
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
